import { createContext, useContext, useState, useEffect } from "react";

const ThemeContext = createContext(null);

// Shared application tokens. Feature screens can still opt into explicit
// utility classes, but legacy settings/report components now inherit the same
// FirmaFlow blue/navy visual language as the redesigned workspace.
const lightTheme = {
  mode: "light",
  // Backgrounds
  bgPrimary: "bg-[#f7faff]",
  bgSecondary: "bg-white",
  bgCard: "bg-white",
  bgHeader: "bg-[#1677ff]",
  bgHover: "hover:bg-blue-50/70",
  bgInput: "bg-white",
  bgButton: "bg-[#1677ff]",
  bgAccent: "bg-[#f3f7fc]",
  bgBadgeGreen: "bg-green-100",
  bgBadgeRed: "bg-red-100",
  bgBadgeBlue: "bg-blue-100",

  // Text colors
  textPrimary: "text-[#0b1b3a]",
  textSecondary: "text-slate-600",
  textTertiary: "text-slate-500",
  textWhite: "text-white",
  textGreen: "text-green-700",
  textRed: "text-red-700",
  textBlue: "text-blue-700",
  textPurple: "text-[#1677ff]",

  // Borders
  borderPrimary: "border-slate-200",
  borderSecondary: "border-slate-200",
  borderHover: "hover:border-[#1677ff]",

  // Shadows
  shadow: "shadow-[0_12px_32px_rgba(15,42,80,0.07)]",
  shadowSm: "shadow-[0_6px_18px_rgba(15,42,80,0.06)]",

  // Rings
  ring: "focus:ring-[#1677ff]",

  // Gradients
  gradient: "from-[#1677ff] to-[#0868ed]",
  gradientLight: "from-[#eef5ff] to-white",
};

// Dark theme colors
const darkTheme = {
  mode: "dark",
  // Backgrounds
  bgPrimary: "bg-[#04152b]",
  bgSecondary: "bg-[#071d38]",
  bgCard: "bg-[#071d38]",
  bgHeader: "bg-[#0b3f91]",
  bgHover: "hover:bg-white/[0.05]",
  bgInput: "bg-[#09182d]",
  bgButton: "bg-[#1677ff]",
  bgAccent: "bg-[#09182d]",
  bgBadgeGreen: "bg-green-900",
  bgBadgeRed: "bg-red-900",
  bgBadgeBlue: "bg-blue-900",

  // Text colors
  textPrimary: "text-white",
  textSecondary: "text-slate-300",
  textTertiary: "text-slate-500",
  textWhite: "text-white",
  textGreen: "text-green-400",
  textRed: "text-red-400",
  textBlue: "text-blue-400",
  textPurple: "text-[#4d9bff]",

  // Borders
  borderPrimary: "border-[#203553]",
  borderSecondary: "border-[#29405f]",
  borderHover: "hover:border-[#1677ff]",

  // Shadows
  shadow: "shadow-[0_18px_42px_rgba(0,0,0,0.24)]",
  shadowSm: "shadow-[0_10px_28px_rgba(0,0,0,0.2)]",

  // Rings
  ring: "focus:ring-[#1677ff]",

  // Gradients
  gradient: "from-[#1677ff] to-[#0868ed]",
  gradientLight: "from-[#071d38] to-[#09182d]",
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage for saved theme preference
    const saved = localStorage.getItem("firmaflow-theme");
    return saved === "dark";
  });

  const theme = isDarkMode ? darkTheme : lightTheme;

  useEffect(() => {
    // Save theme preference to localStorage
    localStorage.setItem("firmaflow-theme", isDarkMode ? "dark" : "light");

    // Update document class for global styles if needed
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode((prev) => !prev);
  };

  const value = {
    theme,
    isDarkMode,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

// Custom hook to use theme
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
